import { Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Text } from '@/components/ui/text';
import { Icon } from '~/global/components/icon/Icon';
import { useHealth } from '~/health/contexts/health/Health';
import {
  HealthDataSource,
  HealthPermissionStatus,
} from '~/health/lib/health-provider/health-provider';

/**
 * The single "Connected data" integrations entry on the Profile screen — the
 * shared connect home for the health-store integration (#29/#30/#36). Renders
 * the not-connected and connected states and hides entirely where no health
 * store exists (web / unsupported), so nothing breaks off-platform.
 *
 * Built to the committed design in `fytness.pen`. Reads are stubbed at the
 * provider layer in this slice; tapping Connect runs the real OS permission
 * request via `useHealth().connect()`.
 */
export const HealthConnectCard = () => {
  const { t } = useTranslation('user');
  const {
    isAvailable,
    isConnected,
    isPartiallyConnected,
    isRequesting,
    permissionStatus,
    source,
    connect,
    openSettings,
  } = useHealth();

  // Hidden anywhere health access is unavailable: web (synchronous, no store)
  // and e.g. Android without Health Connect (surfaces async as UNAVAILABLE).
  if (!isAvailable || permissionStatus === HealthPermissionStatus.UNAVAILABLE) {
    return null;
  }

  const providerName =
    source === HealthDataSource.HEALTH_CONNECT
      ? t('settings.integrations.healthConnect')
      : t('settings.integrations.appleHealth');

  // Once anything is shared (full or partial), the row switches to a status +
  // Manage affordance; otherwise it offers Connect.
  const showManage = isConnected || isPartiallyConnected;

  return (
    <View
      testID="health-connect-card"
      className="bg-surface rounded-2xl p-4 xl:p-6 gap-4"
    >
      <Text className="text-sm xl:text-base font-semibold">
        {t('settings.integrations.title')}
      </Text>

      <View className="flex-row items-center justify-between gap-3 bg-surface-elevated rounded-lg p-3">
        <View className="flex-row items-center gap-3 flex-1">
          <View className="w-10 h-10 rounded-lg bg-accent-soft items-center justify-center">
            <Icon name="HeartPulse" size={20} className="text-accent" />
          </View>
          <View className="flex-1 gap-0.5">
            <Text className="text-sm font-semibold">{providerName}</Text>
            {showManage ? (
              <View className="flex-row items-center gap-1.5">
                <View
                  className={cn(
                    'w-1.5 h-1.5 rounded-full',
                    isConnected ? 'bg-positive' : 'bg-accent',
                  )}
                />
                <Text
                  className={cn(
                    'text-xs font-medium',
                    isConnected ? 'text-positive' : 'text-accent',
                  )}
                >
                  {isConnected
                    ? t('settings.integrations.connected')
                    : t('settings.integrations.partiallyConnected')}
                </Text>
              </View>
            ) : (
              <Text className="text-xs text-text-secondary">
                {t('settings.integrations.description')}
              </Text>
            )}
          </View>
        </View>

        {showManage ? (
          <Pressable
            testID="health-manage-button"
            onPress={() => void openSettings()}
            hitSlop={8}
            className="flex-row items-center gap-1"
          >
            <Text className="text-sm font-medium text-text-secondary">
              {t('settings.integrations.manage')}
            </Text>
            <Icon
              name="ChevronRight"
              size={16}
              className="text-text-secondary"
            />
          </Pressable>
        ) : (
          <Pressable
            testID="health-connect-button"
            onPress={() => void connect()}
            disabled={isRequesting}
            hitSlop={8}
            className={cn(
              'flex-row items-center gap-1.5 rounded-lg bg-accent px-3.5 py-2',
              isRequesting && 'opacity-50',
            )}
          >
            <Icon name="Plus" size={14} className="text-accent-text" />
            <Text className="text-xs font-semibold text-accent-text">
              {isRequesting
                ? t('settings.integrations.connecting')
                : t('settings.integrations.connect')}
            </Text>
          </Pressable>
        )}
      </View>

      {!showManage && (
        <Text className="text-xs text-text-secondary">
          {t('settings.integrations.caption')}
        </Text>
      )}
    </View>
  );
};
